import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { formatMonth } from '../streamingFormat';
import { sourceNames } from '../streamingSources';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
import { streamingStateMessages } from '../streamingStateMessages';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EarningsEmptyCardProps {
  /**
   * `noSource`: no connected source reports earnings. `nothing`: nothing reported. `noMonth`: no
   * whole month.
   */
  kind: 'noSource' | 'nothing' | 'noMonth';
  months?: { from: string; to: string } | null;
  /** `noSource`: the kinds of source that report earnings (`integrationType`), for the neutral line. */
  givenBy?: readonly string[];
  onOpenCatalog: () => void;
}

/** The empty states of Earnings (design e3). */
const EarningsEmptyCardComponent: React.FC<EarningsEmptyCardProps> = ({ kind, months, givenBy, onOpenCatalog }) => {
  const intl = useIntl();
  if (kind === 'noSource') {
    return (
      <Panel nested className={styles.card} data-testid="streaming-earnings-no-source">
        <h3>{intl.formatMessage(messages.noSourceTitle)}</h3>
        <p>{intl.formatMessage(messages.noSourceText, { sources: sourceNames(givenBy ?? []) })}</p>
        <SMLButton variant="primary" onClick={onOpenCatalog}>
          {intl.formatMessage(streamingStateMessages.connectSource)}
        </SMLButton>
      </Panel>
    );
  }
  if (kind === 'noMonth' || months === null || months === undefined) {
    return (
      <Panel nested className={styles.card} data-testid="streaming-earnings-no-month">
        <h3>{intl.formatMessage(messages.nothingTitle)}</h3>
        <p>{intl.formatMessage(messages.noWholeMonth)}</p>
      </Panel>
    );
  }
  return (
    <Panel nested className={styles.card} data-testid="streaming-earnings-nothing">
      <h3>{intl.formatMessage(messages.nothingTitle)}</h3>
      <p>
        {intl.formatMessage(messages.nothingText, {
          start: formatMonth(intl, months.from),
          end: formatMonth(intl, months.to),
        })}
      </p>
    </Panel>
  );
};

export const EarningsEmptyCard = TypedMemo(EarningsEmptyCardComponent);
